import React from "react";
import {
  ArrowRight,
  ChevronRight,
  CreditCard,
  ShieldCheck,
  BadgeCheck,
  Users,
  Fingerprint,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const Features = () => {
  const features = [
    {
      icon: <ShieldCheck size={24} className="text-blue-600" />,
      title: "Bank-Level Security",
      description:
        "Enterprise-grade encryption and security measures to protect your money and personal information at all times.",
    },
    {
      icon: <BadgeCheck size={24} className="text-blue-600" />,
      title: "KYC Verification",
      description:
        "Every user is verified through our strict KYC process ensuring a safe and trustworthy community.",
    },
    {
      icon: <Users size={24} className="text-blue-600" />,
      title: "Group Savings",
      description:
        "Start or join savings groups with friends, family or colleagues with complete transparency.",
    },
    {
      icon: <CreditCard size={24} className="text-blue-600" />,
      title: "Easy Payments",
      description:
        "Secure and seamless payment options with instant notifications and transaction history.",
    },
    {
      icon: <Fingerprint size={24} className="text-blue-600" />,
      title: "Biometric Protection",
      description:
        "Enhanced security with fingerprint and face recognition to authorize transactions.",
    },
    {
      icon: <TrendingUp size={24} className="text-blue-600" />,
      title: "Financial Insights",
      description:
        "Track your savings growth and analyze your financial progress with detailed analytics.",
    },
  ];

  return (
    <section
      id="features"
      className="py-24 px-4 bg-gradient-to-b from-white to-gray-100"
    >
      <div className="container mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div
            className="inline-flex items-center px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg border border-blue-400 text-white text-xs font-sans font-semibold mb-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
            style={{ textShadow: "0 1px 2px rgba(0,0,0,0.1)" }}
          >
            <ShieldCheck size={12} className="mr-2 drop-shadow-sm" />
            Safety First, Always
          </div>
          <h2
            className="text-3xl md:text-4xl lg:leading-[2.5] font-sans font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-500 mb-4"
            style={{ textShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
          >
            Safe, secure, and truly digital committee
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed font-sans font-medium">
            Our app simplifies the traditional digital committee process with modern
            technology, making it accessible and secure for everyone.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group perspective"
            >
              <div
                className="feature-card relative p-6 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 border border-blue-200 transition-all duration-500 transform hover:rotate-y-6 hover:scale-105 h-full"
                style={{
                  transformStyle: "preserve-3d",
                  boxShadow: "0 10px 30px rgba(0, 0, 255, 0.15)",
                }}
              >
                {/* 3D card effect elements */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-tr from-blue-400/5 to-indigo-400/10 opacity-50 pointer-events-none"></div>
                
                {/* Card shine effect */}
                <div 
                  className="absolute inset-0 rounded-xl opacity-30 pointer-events-none" 
                  style={{
                    background: "linear-gradient(145deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 60%)",
                    transform: "translateZ(4px)",
                  }}
                ></div>
                
                {/* Card edge shadow */}
                <div 
                  className="absolute -bottom-2 left-0 right-0 h-2 mx-4 rounded-b-xl opacity-30" 
                  style={{
                    transform: "translateZ(-1px)",
                    background: "linear-gradient(to bottom, rgba(0,0,0,0.1), transparent)",
                  }}
                ></div>

                {/* Card content with 3D effect */}
                <div className="relative flex flex-col h-full" style={{ transform: "translateZ(20px)" }}>
                  {/* Chip Icon - Credit card like element */}
                  <div className="absolute top-4 right-4 w-10 h-6 rounded-md bg-gradient-to-b from-yellow-300 to-yellow-400 flex items-center justify-center" style={{ boxShadow: "inset 0 1px 1px rgba(255,255,255,0.4), 0 1px 2px rgba(0,0,0,0.2)" }}>
                    <div className="w-8 h-4 bg-gradient-to-br from-yellow-200 to-yellow-100 rounded-sm opacity-70" style={{ backgroundSize: "4px 4px" }}></div>
                  </div>

                  {/* Feature Icon - Using the same icons but enhanced */}
                  <div className="mb-4 mt-10">
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg" style={{ transform: "translateZ(10px)" }}>
                      {React.cloneElement(feature.icon, {
                        size: 24,
                        className: "text-white",
                      })}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-sans font-semibold text-blue-900 mb-2 mt-4">
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="text-blue-700 text-sm font-sans leading-relaxed mb-6">
                    {feature.description}
                  </p>

                  {/* Card Number-like detail with more styling */}
                  <div className="mt-auto flex items-center justify-between">
                    <div className="text-xs font-sans tracking-widest text-blue-500 opacity-80 py-1 px-2 bg-blue-50 rounded-md" style={{ letterSpacing: "0.2em" }}>
                      **** **** **** {String(index + 1000).slice(-4)}
                    </div>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
                      <ChevronRight size={16} className="text-white" />
                    </div>
                  </div>

                  {/* Holographic effect */}
                  <div className="absolute bottom-4 right-16 w-10 h-10 rounded-full opacity-30 bg-gradient-to-r from-blue-300 via-purple-300 to-pink-300" style={{ filter: "blur(4px)" }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 text-center">
          {/* Button removed as in the original */}
        </div>
      </div>
    </section>
  );
};

export default Features;